import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Suspense,
  lazy,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useContextStore } from "@/store/context";
import Layout from "@/app/Layout";
import NotificationProvider, { toast } from "@/providers/NotificationProvider";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useAuth } from "@/providers/AuthProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { getConsoleUrl } from "@/config/runtime";
import {
  openExternalUrl,
} from "@/integrations/tauri/runtimeConfig";
import {
  installDesktopUpdate,
  onDesktopUpdateAvailable,
} from "@/integrations/tauri/updater";
import BrandLogo from "@/components/shell/BrandLogo";
import LanguageSwitcher from "@/components/shell/LanguageSwitcher";
import ThemeSwitcher from "@/components/shell/ThemeSwitcher";
import EnrollmentScreen from "@/components/auth/EnrollmentScreen";
import HankoAuthElement from "@/components/auth/HankoAuthElement";
import { standaloneSetupAPI } from "@/services/api/standaloneSetup";
import type { StandaloneSetupStatus } from "@/services/api/standaloneSetup";
import { llmAPI } from "@/services/api/llm";
import type { OrganizationLLMResolvedConfig } from "@/services/api/runtimeTypes";
import {
  getStoredAccessMode,
  inferAccessModeFromServer,
  setStoredAccessMode,
} from "@/services/accessMode";
import { needsStandaloneOnboarding } from "@/app/onboardingRoute";
import {
  getHankoElementsLanguage,
  registerHankoElements,
} from "@/services/hankoAuth";


const CatalogPage = lazy(() => import("@/pages/operate/CatalogPage"));
const ChatPage = lazy(() => import("@/pages/operate/ChatPage"));
const HistoryPage = lazy(() => import("@/pages/operate/HistoryPage"));
const WelcomePage = lazy(() => import("@/pages/WelcomePage"));
const AgentSpecStudioChatPage = lazy(
  () => import("@/pages/calisto/AgentSpecStudioChatPage"),
);
const AgentSpecPage = lazy(() => import("@/pages/build/AgentSpecPage"));
const AccessPage = lazy(() => import("@/pages/build/AccessPage"));
// CapabilitiesPage removed — content available via Agents and other pages
const ApiKeysPage = lazy(() => import("@/pages/build/ApiKeysPage"));
const AgentControlPage = lazy(() => import("@/pages/calisto/AgentControlPage"));
const ImportExportPage = lazy(() => import("@/pages/build/ImportExportPage"));
const LLMPage = lazy(() => import("@/pages/build/LLMPage"));
const MCPPage = lazy(() => import("@/pages/build/MCPPage"));
const IntegrationsPage = lazy(() => import("@/pages/build/IntegrationsPage"));
const SkillsPage = lazy(() => import("@/pages/build/SkillsPage"));
const InternalRuntimePage = lazy(() => import("@/pages/build/InternalRuntimePage"));
const MessagingPage = lazy(() => import("@/pages/build/MessagingPage"));
const MailCatcherPage = lazy(() => import("@/pages/build/MailCatcherPage"));
const SpacesPage = lazy(() => import("@/pages/build/SpacesPage"));
const VariablesPage = lazy(() => import("@/pages/build/VariablesPage"));
const DataPage = lazy(() => import("@/pages/build/DataPage"));

function AuthGate() {
  const { t, i18n } = useTranslation();
  const {
    authError,
    authMode,
    defaultLocalUser,
    loading,
    isAuthenticated,
    localUsers,
    login,
  } = useAuth();
  const consoleUrl = getConsoleUrl();
  const [selectedLocalUser, setSelectedLocalUser] = useState("");
  const [hankoError, setHankoError] = useState<string | null>(null);
  const hankoLanguage = getHankoElementsLanguage(
    i18n.resolvedLanguage || i18n.language,
  );

  useEffect(() => {
    if (authMode !== "local") {
      return;
    }
    if (selectedLocalUser) {
      return;
    }
    setSelectedLocalUser(defaultLocalUser || localUsers[0]?.email || "");
  }, [authMode, defaultLocalUser, localUsers, selectedLocalUser]);

  useEffect(() => {
    if (authMode !== "hanko") {
      return;
    }
    registerHankoElements().catch((err) => {
      setHankoError(err instanceof Error ? err.message : t("auth.signInError"));
    });
  }, [authMode, t]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        {t("auth.checking")}
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  if (authMode === "hanko") {
    return (
      <div className="flex h-screen items-center justify-center bg-background px-6">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="space-y-3">
              <BrandLogo className="max-w-[12rem]" />
              <h1 className="text-2xl font-semibold">AgentMaurice OS</h1>
            </div>
            <div className="flex items-center gap-3">
              <ThemeSwitcher compact />
              <LanguageSwitcher compact />
            </div>
          </div>

          {(hankoError || authError) && (
            <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {hankoError || authError}
            </div>
          )}
          <div className="mt-5">
            <HankoAuthElement lang={hankoLanguage} />
          </div>

          <p className="mt-5 text-center text-sm text-muted-foreground">
            {t("auth.noAccountYet")}{" "}
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={() => void openExternalUrl(consoleUrl)}
            >
              {t("auth.createAccount")}
            </button>
          </p>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {t("auth.createAccountOnConsoleHint")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="space-y-3">
            <BrandLogo className="max-w-[12rem]" />
            <h1 className="text-2xl font-semibold">AgentMaurice OS</h1>
          </div>
          <div className="flex items-center gap-3">
            <ThemeSwitcher compact />
            <LanguageSwitcher compact />
          </div>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("auth.required")}
        </p>
        {authMode === "local" && (
          <div className="mt-4 space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {t("auth.localModeEnabled")}
            </div>
            {localUsers.length > 0 && (
              <label className="block text-sm">
                <span className="mb-1 block text-muted-foreground">
                  {t("auth.localAccount")}
                </span>
                <select
                  className="w-full rounded-lg border border-border bg-background px-3 py-2"
                  value={selectedLocalUser}
                  onChange={(event) => setSelectedLocalUser(event.target.value)}
                >
                  {localUsers.map((account) => (
                    <option key={account.email} value={account.email}>
                      {account.name || account.email} ({account.role})
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
        {authError && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {authError}
          </div>
        )}
        <div className="mt-6 flex gap-3">
          <Button
            onClick={() =>
              void login(
                authMode === "local"
                  ? selectedLocalUser || undefined
                  : undefined,
              )
            }
            className="flex-1"
          >
            {authMode === "local" ? t("auth.localSignIn") : t("auth.signIn")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RequireAccess({
  when,
  children,
  redirectTo = "/operate/chat",
  fallback,
}: {
  when: boolean;
  children: ReactElement;
  redirectTo?: string;
  fallback?: ReactElement;
}) {
  if (!when) {
    if (fallback) {
      return fallback;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

function AccessBlockedPage({
  title,
  description,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
}: {
  title: string;
  description: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel?: string;
  secondaryHref?: string;
}) {
  const navigate = useNavigate();

  return (
    <div className="min-h-full bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.10),_transparent_26%),radial-gradient(circle_at_bottom_right,_rgba(245,158,11,0.08),_transparent_24%),linear-gradient(180deg,_rgba(255,255,255,0.96),_rgba(248,250,252,0.94))] px-6 py-8 dark:bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.16),_transparent_26%),radial-gradient(circle_at_bottom_right,_rgba(245,158,11,0.10),_transparent_24%),linear-gradient(180deg,_rgba(9,12,18,0.96),_rgba(15,23,42,0.94))]">
      <div className="mx-auto flex min-h-[calc(100vh-8rem)] max-w-4xl items-center justify-center">
        <Card className="w-full rounded-[28px] border-border/70 bg-card/90 p-8 shadow-xl shadow-agent-indigo/5 backdrop-blur-sm">
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                Build
              </p>
              <h1 className="text-3xl font-semibold tracking-tight text-foreground">
                {title}
              </h1>
              <p className="max-w-2xl text-sm leading-7 text-muted-foreground">
                {description}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate(primaryHref)}>
                {primaryLabel}
              </Button>
              {secondaryHref && secondaryLabel ? (
                <Button
                  variant="outline"
                  onClick={() => navigate(secondaryHref)}
                >
                  {secondaryLabel}
                </Button>
              ) : null}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function DesktopUpdateNotifier() {
  const { t } = useTranslation();

  useEffect(() => {
    let active = true;
    let cleanup: (() => void) | null = null;

    void onDesktopUpdateAvailable(({ version }) => {
      const toastId = toast(
        (toastState) => (
          <div className="flex max-w-sm items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                {t("desktopUpdate.availableTitle")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("desktopUpdate.availableDescription", {
                  version: version || t("desktopUpdate.genericVersion"),
                })}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                toast.dismiss(toastState.id);
                const loadingId = toast.loading(t("desktopUpdate.installing"));
                void installDesktopUpdate()
                  .then(() => {
                    toast.dismiss(loadingId);
                  })
                  .catch((error) => {
                    toast.dismiss(loadingId);
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : t("desktopUpdate.installError"),
                    );
                  });
              }}
            >
              {t("desktopUpdate.installAction")}
            </Button>
          </div>
        ),
        { duration: Infinity, id: `desktop-update-${version || "latest"}` },
      );
      return toastId;
    }).then((unlisten) => {
      if (!active) {
        unlisten?.();
        return;
      }
      cleanup = unlisten;
    });

    return () => {
      active = false;
      cleanup?.();
    };
  }, [t]);

  return null;
}

function StandaloneWelcomeBootstrap() {
  const location = useLocation();
  const navigate = useNavigate();
  const orgId = useContextStore((state) => state.org);
  const deploymentId = useContextStore((state) => state.deploymentId);
  const contextLoading = useContextStore((state) => state.isLoading);
  const { loading, isAuthenticated, needsEnrollment } = useAuth();
  const statusCacheRef = useRef<{
    key: string;
    at: number;
    status: StandaloneSetupStatus;
  } | null>(null);
  const hostedConfigCacheRef = useRef<{
    key: string;
    at: number;
    resolved: OrganizationLLMResolvedConfig | null;
  } | null>(null);
  const canUseWithoutProvider = location.pathname.startsWith("/build/mcp");

  useEffect(() => {
    if (
      loading ||
      contextLoading ||
      !isAuthenticated ||
      needsEnrollment
    ) {
      return;
    }

    let active = true;

    const bootstrap = async () => {
      const storedAccessMode = getStoredAccessMode();

      try {
        const now = Date.now();
        const cachedStatus = statusCacheRef.current;
        const statusCacheKey = `${orgId || ""}:${deploymentId || ""}`;
        let status: StandaloneSetupStatus;
        if (
          cachedStatus?.key === statusCacheKey &&
          now - cachedStatus.at < 15_000
        ) {
          status = cachedStatus.status;
        } else {
          status = await standaloneSetupAPI.getStatus({
            organizationId: orgId || undefined,
            deploymentId: deploymentId || undefined,
          });
          statusCacheRef.current = { key: statusCacheKey, at: now, status };
        }
        if (!active) {
          return;
        }

        let resolvedLLMConfig: OrganizationLLMResolvedConfig | null = null;
        const effectiveOrganizationId = orgId || status.organizationId || "";
        const effectiveDeploymentId = deploymentId || status.deploymentId || "";
        if (effectiveOrganizationId && effectiveDeploymentId) {
          try {
            const cacheKey = `${effectiveOrganizationId}:${effectiveDeploymentId}`;
            const cachedConfig = hostedConfigCacheRef.current;
            if (cachedConfig?.key === cacheKey && now - cachedConfig.at < 15_000) {
              resolvedLLMConfig = cachedConfig.resolved;
            } else {
              const llmConfig = await llmAPI.getDeploymentConfig(
                effectiveOrganizationId,
                effectiveDeploymentId,
              );
              resolvedLLMConfig = llmConfig.resolved ?? null;
              hostedConfigCacheRef.current = {
                key: cacheKey,
                at: now,
                resolved: resolvedLLMConfig,
              };
            }
          } catch {
            resolvedLLMConfig = null;
          }
        }

        const serverAccessMode = inferAccessModeFromServer(status, resolvedLLMConfig);
        const accessMode = serverAccessMode || storedAccessMode;
        if (serverAccessMode && storedAccessMode !== serverAccessMode) {
          setStoredAccessMode(serverAccessMode);
        } else if (!storedAccessMode && accessMode) {
          setStoredAccessMode(accessMode);
        }

        const needsWelcome = needsStandaloneOnboarding({
          status,
          resolvedLLMConfig,
          storedAccessMode: accessMode,
          organizationId: orgId,
          deploymentId,
          allowMissingProvider: canUseWithoutProvider,
        });

        if (needsWelcome && location.pathname !== "/welcome") {
          navigate("/welcome", { replace: true });
          return;
        }

        if (!needsWelcome && location.pathname === "/welcome") {
          navigate(`/operate/chat${location.search}`, { replace: true });
        }
      } catch {
        if (!active) {
          return;
        }

        const needsWelcome =
          !storedAccessMode ||
          !orgId ||
          !deploymentId;

        if (
          needsWelcome &&
          !canUseWithoutProvider &&
          location.pathname !== "/welcome"
        ) {
          navigate("/welcome", { replace: true });
        }
      }
    };

    void bootstrap();

    return () => {
      active = false;
    };
  }, [
    contextLoading,
    deploymentId,
    isAuthenticated,
    loading,
    location.pathname,
    location.search,
    navigate,
    needsEnrollment,
    orgId,
    canUseWithoutProvider,
  ]);

  return null;
}

function RedirectWithSearch({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

export default function App() {
  const { t } = useTranslation();
  const location = useLocation();
  const setPosture = useContextStore((state) => state.setPosture);
  const { isAuthenticated, loading, needsEnrollment } = useAuth();
  const { canRead, canWrite, canDeploy, canManageUsers } = usePermissions();
  const canBuildRecipes = canWrite("recipes");
  const canBuildDeploy = canDeploy();
  const canBuildExternalControl = canManageUsers();
  const canAccessBuild = useMemo(
    () =>
      canBuildDeploy ||
      canBuildExternalControl,
    [
      canBuildDeploy,
      canBuildExternalControl,
    ],
  );
  const defaultBuildRoute = useMemo(() => {
    if (canBuildDeploy) {
      return "/build/access";
    }
    if (canBuildExternalControl) {
      return "/build/api-keys";
    }
    if (canBuildRecipes) {
      return "/studio/agent-spec";
    }
    return "/operate/chat";
  }, [
    canBuildDeploy,
    canBuildExternalControl,
    canBuildRecipes,
  ]);
  const buildFallbackHref =
    defaultBuildRoute.startsWith("/build/") && canAccessBuild
      ? defaultBuildRoute
      : "/operate/chat";
  const buildFallbackLabel =
    defaultBuildRoute.startsWith("/build/") && canAccessBuild
      ? t("build.accessPrimaryCta")
      : t("build.accessBackToRun");
  const buildBackToRunLabel = t("build.accessBackToRun");
  const buildAccessFallback = (
    title: string,
    description: string,
  ) => (
    <AccessBlockedPage
      title={title}
      description={description}
      primaryHref={buildFallbackHref}
      primaryLabel={buildFallbackLabel}
      secondaryHref={
        buildFallbackHref !== "/operate/chat" ? "/operate/chat" : undefined
      }
      secondaryLabel={
        buildFallbackHref !== "/operate/chat"
          ? buildBackToRunLabel
          : undefined
      }
    />
  );

  // Update posture based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/build")) {
      setPosture("build");
    } else if (path.startsWith("/studio")) {
      setPosture("calisto");
    } else {
      setPosture("operate");
    }
  }, [location.pathname, setPosture]);

  return (
    <NotificationProvider>
      <DesktopUpdateNotifier />
      <AuthGate />
      {!loading && isAuthenticated && needsEnrollment && <EnrollmentScreen />}
      {!loading && isAuthenticated && !needsEnrollment && (
        <>
          <StandaloneWelcomeBootstrap />
          <Suspense
            fallback={
              <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
                {t("auth.loadingApp")}
              </div>
            }
          >
            <Routes>
              <Route path="/welcome" element={<WelcomePage />} />
              <Route element={<Layout />}>
                <Route
                  path="/"
                  element={<RedirectWithSearch to="/operate/chat" />}
                />
                <Route
                  path="/operate/catalog"
                  element={
                    <RequireAccess when={canRead("recipes")}>
                      <CatalogPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/operate/chat"
                  element={
                    <RequireAccess when={canRead("executions")}>
                      <ChatPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/operate/history"
                  element={
                    <RequireAccess when={canRead("executions")}>
                      <HistoryPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio"
                  element={<RedirectWithSearch to="/studio/chat" />}
                />
                <Route
                  path="/studio/chat"
                  element={
                    <RequireAccess
                      when={canBuildRecipes}
                      fallback={buildAccessFallback(
                        t("build.accessRecipesTitle"),
                        t("build.accessRecipesDescription"),
                      )}
                    >
                      <AgentSpecStudioChatPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/studio/agent-spec"
                  element={
                    <RequireAccess
                      when={canBuildRecipes}
                      fallback={buildAccessFallback(
                        t("build.accessRecipesTitle"),
                        t("build.accessRecipesDescription"),
                      )}
                    >
                      <AgentSpecPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/api-keys"
                  element={
                    <RequireAccess
                      when={canBuildExternalControl}
                      fallback={buildAccessFallback(
                        t("build.accessExternalControlTitle"),
                        t("build.accessExternalControlDescription"),
                      )}
                    >
                      <ApiKeysPage />
                    </RequireAccess>
                  }
                />
                {/* Legacy path: keys now live at /build/api-keys, agent control in Studio. */}
                <Route
                  path="/build/external-control"
                  element={<RedirectWithSearch to="/build/api-keys" />}
                />
                <Route
                  path="/studio/agent-control"
                  element={
                    <RequireAccess
                      when={canBuildExternalControl}
                      fallback={buildAccessFallback(
                        t("build.accessExternalControlTitle"),
                        t("build.accessExternalControlDescription"),
                      )}
                    >
                      <AgentControlPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/access"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <AccessPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/import-export"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <ImportExportPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/llm"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <LLMPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/mcp"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <MCPPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/integrations"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <IntegrationsPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/skills"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <SkillsPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/internal"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <InternalRuntimePage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/messaging"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <MessagingPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/mailcatcher"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <MailCatcherPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/spaces"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <SpacesPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/variables"
                  element={
                    <RequireAccess
                      when={canBuildDeploy}
                      fallback={buildAccessFallback(
                        t("build.accessDeployTitle"),
                        t("build.accessDeployDescription"),
                      )}
                    >
                      <VariablesPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/build/data"
                  element={
                    <RequireAccess
                      when={canBuildRecipes}
                      fallback={buildAccessFallback(
                        t("build.accessRecipesTitle"),
                        t("build.accessRecipesDescription"),
                      )}
                    >
                      <DataPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="*"
                  element={<Navigate to="/operate/chat" replace />}
                />
              </Route>
            </Routes>
          </Suspense>
        </>
      )}
    </NotificationProvider>
  );
}
